/**
 * Helper that takes an rgb integer in the 0-255 range and converts it to the 0-1 float range
 */
export function rgbInteger2float(i) {
    return i / 255;
}
/**
 * Helper that takes an rgb float in the 0-1 range and converts it to the 0-255 integer range
 */
export function rgbFloat2Integer(f) {
    return Math.round(f * 255);
}
/**
 * Helper for the rgb2gray function below. y is a value from 0-1.
 */
function srgb2linear(y) {
    if (y <= 0.04045) {
        return y / 12.95;
    }
    return Math.pow(((y + 0.055) / 1.055), 2.4);
}
/**
 * This conversion uses the luminosity method, returning the value for gray (from 0-255)
 * https://stackoverflow.com/questions/56198778/what-is-the-efficient-way-to-calculate-human-eye-contrast-difference-for-rgb-val/56200738#56200738
 * https://en.wikipedia.org/wiki/SRGB#The_reverse_transformation
 */
export function rgb2gray(rgb) {
    const rLinear = srgb2linear(rgbInteger2float(rgb.r));
    const gLinear = srgb2linear(rgbInteger2float(rgb.g));
    const bLinear = srgb2linear(rgbInteger2float(rgb.b));
    return rLinear * 0.2126 + gLinear * 0.7152 + bLinear * 0.0722;
}
function validateHexInput(hex) {
    if (!hex.match(/^#([a-f0-9]{3,4}|[a-f0-9]{6}|[a-f0-9]{8})$/i)) {
        throw new Error('Invalid hex input, must be a 3, 4, 6 or 8 character long hexadecimal number preceded with a #');
    }
}
/**
 * Helper that converts any hex to a 6 digit version. For a 8 digit hex, the last 2 characters present the alpha-channel in an hexadecimal format.
 * The 4 digit-version is a shorter version for the 8 digit one. 3 digits are the shorter versions for the regular 6 digit hex, so without an alpha channel.
 */
export function to6DigitHex(hex) {
    const hexValue = hex.replace('#', '');
    if (hexValue.length === 3 || hexValue.length === 4) {
        return hexValue.replace(/([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])/, '$1$1$2$2$3$3').substring(0, 6);
    }
    if (hexValue.length === 8) {
        return hexValue.substring(0, 6);
    }
    return hexValue;
}
/**
 * Gets the alpha value from an hex. As this only works for 8 or 4 digit hexes, it returns the full 255 when it isn't one of those 2 lengths.
 */
export function getAlphaForHex(hex) {
    const hexValue = hex.replace('#', '');
    if (hexValue.length === 4) {
        return parseInt(hexValue.substring(3, 5), 16);
    }
    if (hexValue.length === 8) {
        return parseInt(hexValue.substring(6, 9), 16);
    }
    return 255;
}
// H ranges from 0 to 360, and s and v ranges from 0 to 1
export function rgb2hsv(rgb) {
    const r = rgbInteger2float(rgb.r);
    const g = rgbInteger2float(rgb.g);
    const b = rgbInteger2float(rgb.b);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = max * 360;
    let s = max;
    const v = max;
    const d = max - min;
    s = max === 0 ? 0 : d / max;
    if (max === min) {
        h = 0; // achromatic
    }
    else {
        switch (max) {
            case r:
                h = 60 * ((g - b) / d + (g < b ? 6 : 0));
                break;
            case g:
                h = 60 * ((b - r) / d + 2);
                break;
            case b:
            default:
                h = 60 * ((r - g) / d + 4);
                break;
        }
    }
    return { h, s, v };
}
export function hsv2rgb(hsv) {
    const { h, s, v } = hsv;
    const c = v * s;
    const k = h / 60;
    const x = c * (1 - Math.abs((k % 2) - 1));
    let r1 = 0;
    let g1 = 0;
    let b1 = 0;
    if (k >= 0 && k <= 1) {
        r1 = c;
        g1 = x;
    }
    if (k > 1 && k <= 2) {
        r1 = x;
        g1 = c;
    }
    if (k > 2 && k <= 3) {
        g1 = c;
        b1 = x;
    }
    if (k > 3 && k <= 4) {
        g1 = x;
        b1 = c;
    }
    if (k > 4 && k <= 5) {
        r1 = x;
        b1 = c;
    }
    if (k > 5 && k <= 6) {
        r1 = c;
        b1 = x;
    }
    const m = v - c;
    return { r: rgbFloat2Integer(r1 + m), g: rgbFloat2Integer(g1 + m), b: rgbFloat2Integer(b1 + m) };
}
// http://www.brucelindbloom.com/index.html?Eqn_RGB_XYZ_Matrix.html using the srgb matrix
export function rgb2xyz(rgb) {
    let r = rgbInteger2float(rgb.r);
    let g = rgbInteger2float(rgb.g);
    let b = rgbInteger2float(rgb.b);
    r = srgb2linear(r) * 100;
    g = srgb2linear(g) * 100;
    b = srgb2linear(b) * 100;
    const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
    const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
    const z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;
    return { x, y, z };
}
// Using the D65 reference white defined from the srgb matrix conversion above
// http://www.brucelindbloom.com/index.html?Eqn_XYZ_to_Lab.html
export function xyz2lab(xyz) {
    let x = xyz.x / 95.047;
    let y = xyz.y / 100.0;
    let z = xyz.z / 108.883;
    const epsilon = 216 / 24389;
    const kappa = 24389 / 27;
    if (x > epsilon) {
        x = Math.pow(x, 1 / 3);
    }
    else {
        x = (kappa * x + 16) / 116;
    }
    if (y > epsilon) {
        y = Math.pow(y, 1 / 3);
    }
    else {
        y = (kappa * y + 16) / 116;
    }
    if (z > epsilon) {
        z = Math.pow(z, 1 / 3);
    }
    else {
        z = (kappa * z + 16) / 116;
    }
    const l = 116 * y - 16;
    const a = 500 * (x - y);
    const b = 200 * (y - z);
    return { l, a, b };
}
// Merely a helper method for the below hsl2rgb function
function hue2value(pqt) {
    const { p, q } = pqt;
    let { t } = pqt;
    if (t < 0) {
        t += 1;
    }
    if (t > 1) {
        t -= 1;
    }
    if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
        return q;
    }
    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
}
// Expects hsl to have hue as degrees and saturation and lightness in the 0-1 range
export function hsl2rgb(hsl) {
    let r;
    let g;
    let b;
    const { l, s } = hsl;
    const h = hsl.h / 360;
    if (hsl.s === 0) {
        // achromatic
        r = l;
        g = l;
        b = l;
    }
    else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2value({ p, q, t: h + 1 / 3 });
        g = hue2value({ p, q, t: h });
        b = hue2value({ p, q, t: h - 1 / 3 });
    }
    return { r: rgbFloat2Integer(r), g: rgbFloat2Integer(g), b: rgbFloat2Integer(b) };
}
export function rgb2hsl(rgb) {
    const { r, g, b } = rgb;
    const rPrime = rgbInteger2float(r);
    const gPrime = rgbInteger2float(g);
    const bPrime = rgbInteger2float(b);
    const cMax = Math.max(rPrime, gPrime, bPrime);
    const cMin = Math.min(rPrime, gPrime, bPrime);
    const delta = cMax - cMin;
    let h;
    let s;
    const l = (cMax + cMin) / 2;
    if (delta === 0) {
        h = 0;
    }
    else if (cMax === rPrime) {
        h = 60 * (((gPrime - bPrime) / delta) % 6);
    }
    else if (cMax === gPrime) {
        h = 60 * ((bPrime - rPrime) / delta + 2);
    }
    else {
        h = 60 * ((rPrime - gPrime) / delta + 4);
    }
    if (h < 0) {
        h += 360;
    }
    if (delta === 0) {
        s = 0;
    }
    else {
        s = delta / (1 - Math.abs(2 * l - 1));
    }
    return { h, s, l };
}
// RGB2
export function rgb2hex(rgb) {
    return `#${((rgb.r < 16 ? '0' : '') +
        rgb.r.toString(16) +
        (rgb.g < 16 ? '0' : '') +
        rgb.g.toString(16) +
        (rgb.b < 16 ? '0' : '') +
        rgb.b.toString(16)).toUpperCase()}`;
}
export function rgb2rgba(rgb) {
    return { r: rgb.r, g: rgb.g, b: rgb.b, a: 255 };
}
export function rgb2cmyk(rgb) {
    const rPrime = rgbInteger2float(rgb.r);
    const gPrime = rgbInteger2float(rgb.g);
    const bPrime = rgbInteger2float(rgb.b);
    const k = 1 - Math.max(rPrime, gPrime, bPrime);
    let c = 0;
    let m = 0;
    let y = 0;
    if (k !== 1) {
        c = (1 - rPrime - k) / (1 - k);
        m = (1 - gPrime - k) / (1 - k);
        y = (1 - bPrime - k) / (1 - k);
    }
    return {
        c: Math.round(c * 100),
        m: Math.round(m * 100),
        y: Math.round(y * 100),
        k: Math.round(k * 100),
    };
}
// HEX2
export function hex2rgb(hex) {
    validateHexInput(hex);
    const hexValue = to6DigitHex(hex);
    return {
        r: parseInt(hexValue[0] + hexValue[hexValue[3] ? 1 : 0], 16),
        g: parseInt(hexValue[hexValue[3] ? 2 : 1] + (hexValue[3] || hexValue[1]), 16),
        b: parseInt((hexValue[4] || hexValue[2]) + (hexValue[5] || hexValue[2]), 16),
    };
}
export function hex2rgba(hex) {
    const { r, g, b } = hex2rgb(hex);
    const a = getAlphaForHex(hex);
    return { r, g, b, a };
}
export function hex2cmyk(hex) {
    const rgb = hex2rgb(hex);
    return rgb2cmyk(rgb);
}
export function hex2hsl(hex) {
    const rgb = hex2rgb(hex);
    return rgb2hsl(rgb);
}
// HSL2
export function hsl2hex(hsl) {
    const rgb = hsl2rgb(hsl);
    return rgb2hex(rgb);
}
// CMYK2
export function cmyk2rgb(cmyk) {
    const computedK = cmyk.k / 100;
    const r = 1 - Math.min(1, (cmyk.c / 100) * (1 - computedK) + computedK);
    const g = 1 - Math.min(1, (cmyk.m / 100) * (1 - computedK) + computedK);
    const b = 1 - Math.min(1, (cmyk.y / 100) * (1 - computedK) + computedK);
    return {
        r: rgbFloat2Integer(r),
        g: rgbFloat2Integer(g),
        b: rgbFloat2Integer(b),
    };
}
export function cmyk2hex(cmyk) {
    const rgb = cmyk2rgb(cmyk);
    return rgb2hex(rgb);
}
//# sourceMappingURL=conversions.js.map